import React from 'react';
import PropTypes from 'prop-types';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Link as RouterLink } from 'react-router-dom';

const getCountText = (count) => (
  count === 1
    ? '1 project sample is not shown in clonality results.'
    : `${count} project samples are not shown in clonality results.`
);

function MissingClonalitySamplesPanel({
  samplesWithoutSuccessfulComparisons = [],
  isLoading = false,
  isError = false
}) {
  if (isLoading) {
    return (
      <Box
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          p: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}
      >
        <CircularProgress size={16} />
        <Typography variant="caption" color="text.secondary">
          Checking for samples not shown in clonality results...
        </Typography>
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity="warning" sx={{ py: 0.5 }}>
        <Typography variant="caption">
          Could not load samples without completed clonality comparison results. Clonality results are still available.
        </Typography>
      </Alert>
    );
  }

  if (samplesWithoutSuccessfulComparisons.length === 0) {
    return null;
  }

  return (
    <Accordion disableGutters variant="outlined" TransitionProps={{ timeout: 0 }}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="missing-clonality-samples-content"
        id="missing-clonality-samples-header"
        sx={{ px: 1.5, minHeight: 44 }}
      >
        <Stack spacing={0.25}>
          <Typography variant="subtitle2">
            Samples not shown
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {`${getCountText(samplesWithoutSuccessfulComparisons.length)} View samples.`}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1.5 }}>
        <Stack spacing={1} sx={{ maxHeight: 260, overflow: 'auto' }}>
          {samplesWithoutSuccessfulComparisons.map((sample) => (
            <Box
              key={sample.key}
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                p: 1,
                overflowWrap: 'anywhere'
              }}
            >
              <Stack spacing={0.5}>
                <Box>
                  {sample.samplePath ? (
                    <Link
                      component={RouterLink}
                      to={sample.samplePath}
                      aria-label={`Open sample ${sample.sampleName}`}
                      variant="body2"
                      underline="hover"
                    >
                      {sample.sampleName}
                    </Link>
                  ) : (
                    <Typography variant="body2">
                      {sample.sampleName}
                    </Typography>
                  )}
                  {sample.datasetName && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {sample.datasetName}
                    </Typography>
                  )}
                </Box>

                {sample.organism && (
                  <Typography variant="caption" color="text.secondary">
                    {`Organism: ${sample.organism}`}
                  </Typography>
                )}
              </Stack>
            </Box>
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

MissingClonalitySamplesPanel.propTypes = {
  samplesWithoutSuccessfulComparisons: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    sampleName: PropTypes.string.isRequired,
    datasetName: PropTypes.string,
    organism: PropTypes.string,
    samplePath: PropTypes.string
  })),
  isLoading: PropTypes.bool,
  isError: PropTypes.bool
};

export default MissingClonalitySamplesPanel;
